import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import { Button } from "primereact/button";
import { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useApplyCaseRatePropagationMutation,
  useLazyGetCaseRatePropagationPreviewQuery,
  useUpdateCaseRateRulesetGroupMutation,
  useUpdateCaseRateRulesetMutation,
} from "store/queries/pricing";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import {
  formApplicability,
  mapCaseRateFormToRequestBody,
} from "../helpers/mapFormToRequestBody";
import {
  scopeUpdateFields,
  shouldOfferPropagation,
} from "../helpers/ruleApplicability";
import { extractServerErrorMessage } from "../helpers/ruleBucket";
import { useCaseRateCptConflicts } from "../hooks/useCaseRateCptConflicts";
import { useSubmitRuleGroup } from "../hooks/useSubmitRuleGroup";
import { useRuleScopeComplete } from "./RuleApplicabilityField";
import { usePropagateAfterEdit } from "./usePropagateAfterEdit";

interface ISubmitEditCaseRateProps {
  id: string;
  /**
   * CLE-2842 step 2 — the applicability the rule was loaded with. A save that converts the rule
   * (Payer-Provider ↔ Fallback) sends the new `applicability`; any other save sends none.
   */
  savedApplicability?: RulesetApplicability;
}

export function SubmitEditCaseRate({
  id,
  savedApplicability,
}: ISubmitEditCaseRateProps) {
  const {
    getValues,
    formState: { isValid },
  } = useFormContext();
  // CLE-2842 step 2 — a Payer-Provider rule needs its provider and payer, whatever the
  // scope fields' mount timing did to react-hook-form's own validity.
  const scopeComplete = useRuleScopeComplete();
  const navigate = useNavigate();
  const [useUpdateCaseRateRuleset] = useUpdateCaseRateRulesetMutation();
  const [updateCaseRateRulesetGroup] = useUpdateCaseRateRulesetGroupMutation();
  // CLE-2842 — non-null when this rule is edited for every payer in its bucket at once.
  const submitGroup = useSubmitRuleGroup(updateCaseRateRulesetGroup);
  const { hasConflicts } = useCaseRateCptConflicts({ excludeRulesetId: id });

  const { current: toastElement } = useContext(ToastContext);

  const goBackToList = () => navigate("/payer-rules");

  const { openForId, modal } = usePropagateAfterEdit({
    useLazyPreview: useLazyGetCaseRatePropagationPreviewQuery as any,
    useApplyPropagation: useApplyCaseRatePropagationMutation as any,
    onDone: goBackToList,
  });

  const updatePayerRule = async () => {
    const values = getValues();
    let cptCodes: string[];
    let excludedCptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(values.cptCodes ?? []);
      excludedCptCodes = prepareCodesForSubmit(values.excludedCptCodes ?? []);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    const requestBody = mapCaseRateFormToRequestBody({
      ...values,
      cptCodes,
      excludedCptCodes,
    });
    const applicability = formApplicability(values);
    if (submitGroup) {
      await submitGroup(requestBody);
      return;
    }
    try {
      useUpdateCaseRateRuleset({
        ...requestBody,
        ...scopeUpdateFields(applicability, savedApplicability),
        id,
      })
        .unwrap()
        .then(() => {
          toastElement?.show({
            severity: "success",
            summary: "Payer Ruleset updated",
            detail: "Payer Ruleset updated successfully",
          });
          // Only a rule that was already Typical or Fallback has copies; a conversion returns
          // to the list.
          if (shouldOfferPropagation(applicability, savedApplicability)) {
            openForId(id);
          } else {
            goBackToList();
          }
        })
        .catch((error) => {
          if (error.status === 409) {
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail:
                "A case rate ruleset with this provider, payer, and rule name already exists",
            });
          } else if (error.status === 400) {
            const message = extractServerErrorMessage(error);
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail:
                message ||
                "An error has occurred attempting to update the discount ruleset",
            });
          } else {
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail:
                "An error has occurred attempting to update the discount ruleset",
            });
          }
        });
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Update Payer Rule"
          onClick={updatePayerRule}
          disabled={!isValid || !scopeComplete || hasConflicts}
        />
      </div>
      {modal}
    </ItemBar>
  );
}
